import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "const/axios";
import { API_URL } from "const/constants";

// ========= Core API functions =========

export const DEFAULT_TASK_PARAMS = {
  page: 0,
  size: 20,
  //  soonest due date first, then HIGH > MEDIUM > LOW as tiebreaker.
  // sortDirection is ignored for this key
  sortBy: "deadlinePriority",
  sortDirection: "ASC",
};

const normalizeTasksPage = (raw) => {
  if (Array.isArray(raw)) {
    return { content: raw, totalItems: raw.length, totalPages: 1 };
  }
  return {
    content: Array.isArray(raw?.content) ? raw.content : [],
    totalItems: Number(raw?.totalItems) || 0,
    totalPages: Number(raw?.totalPages) || 0,
  };
};

const getTasksAPI = async (params = {}) => {
  const {
    page = DEFAULT_TASK_PARAMS.page,
    size = DEFAULT_TASK_PARAMS.size,
    sortBy = DEFAULT_TASK_PARAMS.sortBy,
    sortDirection = DEFAULT_TASK_PARAMS.sortDirection,
    assignedBy,
    assignedTo,
    status,
    clientId,
    search,
    dueWithinDays,
    orgWide,
  } = params;

  const response = await axios.get(`${API_URL}/tasks${orgWide ? "/all" : ""}`, {
    params: {
      page,
      size,
      sortBy,
      sortDirection,
      ...(assignedBy ? { assignedBy } : {}),
      ...(assignedTo ? { assignedTo } : {}),
      ...(status ? { status } : {}),
      ...(clientId != null && clientId !== "" ? { clientId } : {}),
      ...(search ? { search } : {}),
      ...(dueWithinDays != null ? { dueWithinDays } : {}),
    },
  });

  return normalizeTasksPage(response.data?.data);
};

const getTasksPageAPI = async (params = {}) => {
  const {
    page = DEFAULT_TASK_PARAMS.page,
    size = DEFAULT_TASK_PARAMS.size,
    sortBy = DEFAULT_TASK_PARAMS.sortBy,
    sortDirection = DEFAULT_TASK_PARAMS.sortDirection,
    assignedBy,
    assignedTo,
    status,
    clientId,
    search,
    dueWithinDays,
    orgWide,
  } = params;

  const response = await axios.get(`${API_URL}/tasks/page`, {
    params: {
      page,
      size,
      sortBy,
      sortDirection,
      ...(assignedBy ? { assignedBy } : {}),
      ...(assignedTo ? { assignedTo } : {}),
      ...(status ? { status } : {}),
      ...(clientId != null && clientId !== "" ? { clientId } : {}),
      ...(search ? { search } : {}),
      ...(dueWithinDays != null ? { dueWithinDays } : {}),
      ...(orgWide ? { orgWide: true } : {}),
    },
  });

  return normalizeTasksPage(response.data?.data);
};

/** Mirrors the backend TaskStatus enum, in board column order. */
export const TASK_STATUS_KEYS = ["PENDING", "IN_PROGRESS", "HOLD", "COMPLETED", "CANCELLED"];

/** Every status that is still open work. HOLD counts: it is waiting on the client, not finished. */
export const ACTIVE_TASK_STATUS_FILTER = "PENDING,IN_PROGRESS,HOLD";

/** `{ PENDING: 0, COMPLETED: 2 }` -> `"COMPLETED:2"`. Page 0 is the server default, so it is
 * dropped to keep the query key (and therefore the react-query cache) stable. */
export const serializeStatusPages = (statusPages) =>
  TASK_STATUS_KEYS.filter((status) => Number(statusPages?.[status]) > 0)
    .map((status) => `${status}:${Number(statusPages[status])}`)
    .join(",");

const normalizeTaskGroup = (raw, fallbackSize) => ({
  content: Array.isArray(raw?.content) ? raw.content : [],
  currentPage: Number(raw?.currentPage) || 0,
  totalItems: Number(raw?.totalItems) || 0,
  totalPages: Number(raw?.totalPages) || 0,
  pageSize: Number(raw?.pageSize) || fallbackSize,
});

const normalizeGroupedTasksPage = (raw, fallbackSize) => {
  const statuses =
    Array.isArray(raw?.statuses) && raw.statuses.length ? raw.statuses : TASK_STATUS_KEYS;
  const groups = statuses.reduce((acc, status) => {
    acc[status] = normalizeTaskGroup(raw?.groups?.[status], fallbackSize);
    return acc;
  }, {});
  const summedTotal = statuses.reduce((sum, status) => sum + groups[status].totalItems, 0);
  return {
    statuses,
    groups,
    pageSize: Number(raw?.pageSize) || fallbackSize,
    totalItems: Number(raw?.totalItems) || summedTotal,
  };
};

const getGroupedTasksPageAPI = async (params = {}) => {
  const {
    page = DEFAULT_TASK_PARAMS.page,
    size = DEFAULT_TASK_PARAMS.size,
    sortBy = DEFAULT_TASK_PARAMS.sortBy,
    sortDirection = DEFAULT_TASK_PARAMS.sortDirection,
    assignedBy,
    assignedTo,
    status,
    clientId,
    search,
    dueWithinDays,
    orgWide,
    statusPages,
  } = params;

  const response = await axios.get(`${API_URL}/tasks/page/grouped`, {
    params: {
      page,
      size,
      sortBy,
      sortDirection,
      ...(assignedBy ? { assignedBy } : {}),
      ...(assignedTo ? { assignedTo } : {}),
      ...(status ? { status } : {}),
      ...(clientId != null && clientId !== "" ? { clientId } : {}),
      ...(search ? { search } : {}),
      ...(dueWithinDays != null ? { dueWithinDays } : {}),
      ...(orgWide ? { orgWide: true } : {}),
      ...(statusPages ? { statusPages } : {}),
    },
  });

  return normalizeGroupedTasksPage(response.data?.data, size);
};

const getTaskByIdAPI = async (id) => {
  const response = await axios.get(`${API_URL}/tasks/${id}`);
  return response.data?.data;
};

const createTaskAPI = async (taskData) => {
  const response = await axios.post(`${API_URL}/tasks`, taskData);
  return response.data?.data;
};

const updateTaskAPI = async ({ id, taskData }) => {
  const response = await axios.put(`${API_URL}/tasks/${id}`, taskData);
  return response.data?.data;
};

/** Status only. Sending it through the full update endpoint clears whatever the payload omits. */
const changeTaskStatusAPI = async ({ id, status }) => {
  const response = await axios.patch(`${API_URL}/tasks/${id}/status`, { status });
  return response.data?.data;
};

const deleteTaskAPI = async (id) => {
  const response = await axios.delete(`${API_URL}/tasks/${id}`);
  return response.data;
};

export const useTasks = (params = {}, options = {}) => {
  // Legacy: useTasks(organizationId) accidentally passed a number as options
  const queryParams =
    params != null && typeof params === "object" && !Array.isArray(params)
      ? { ...DEFAULT_TASK_PARAMS, ...params }
      : { ...DEFAULT_TASK_PARAMS };
  const queryOptions =
    params != null && typeof params === "object" && !Array.isArray(params) ? options : {};

  return useQuery({
    queryKey: ["tasks", queryParams],
    queryFn: () => getTasksAPI(queryParams),
    select: (page) => page?.content ?? [],
    refetchOnWindowFocus: false,
    // refetchOnMount must stay "always": the QueryClient default is refetchOnMount:false, so a
    // mutation that invalidates ["tasks"] from another route (Add/Edit Task navigates away first)
    // only marks this query stale, and coming back would serve the pre-mutation list from cache.
    refetchOnMount: "always",
    retry: false,
    enabled: false,
    ...queryOptions,
  });
};

export const useTasksPage = (params = {}, options = {}) => {
  const queryParams = { ...DEFAULT_TASK_PARAMS, ...params };
  return useQuery({
    queryKey: ["tasks", "page", queryParams],
    queryFn: () => getTasksPageAPI(queryParams),
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    retry: false,
    enabled: false,
    ...options,
  });
};

/** One page of `size` tasks per status, so pending and in-progress work is never pushed off the
 * board by a large completed backlog. */
export const useGroupedTasksPage = (params = {}, options = {}) => {
  const queryParams = { ...DEFAULT_TASK_PARAMS, ...params };
  return useQuery({
    queryKey: ["tasks", "page", "grouped", queryParams],
    queryFn: () => getGroupedTasksPageAPI(queryParams),
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    retry: false,
    enabled: false,
    ...options,
  });
};

export const useTaskById = (id, options = {}) => {
  return useQuery({
    queryKey: ["taskDetails", id],
    queryFn: () => getTaskByIdAPI(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    retry: false,
    ...options,
  });
};

export const useCreateTask = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createTaskAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options.onSuccess?.(data, variables, context);
    },
  });
};

export const useUpdateTask = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: updateTaskAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: ["taskDetails", variables.id],
        });
      }
      options.onSuccess?.(data, variables, context);
    },
  });
};

export const useChangeTaskStatus = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: changeTaskStatusAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: ["taskDetails", variables.id] });
      }
      options.onSuccess?.(data, variables, context);
    },
  });
};

export const useDeleteTask = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: deleteTaskAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options.onSuccess?.(data, variables, context);
    },
  });
};

export const useTasksByClient = (clientId, options = {}) => {
  return useQuery({
    queryKey: ["tasks", "client", clientId],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/tasks/clients/${clientId}`);
      return response.data?.data || [];
    },
    enabled: !!clientId,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    retry: false,
    ...options,
  });
};

/** Tasks linked to one module entry (admission/RPL/visa/insurance/skill row). The ["tasks"]
 * prefix keeps this fresh: every task mutation already invalidates that key. */
export const useModuleEntryTasks = (entryType, entryId, options = {}) => {
  return useQuery({
    queryKey: ["tasks", "module-entry", entryType, entryId],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/tasks/module-entry`, {
        params: { entryType, entryId },
      });
      return response.data?.data || [];
    },
    enabled: Boolean(entryType && entryId),
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    retry: false,
    ...options,
  });
};

// ========= Assignees (multi-assignment) =========

const addAssigneeAPI = async ({ id, userId }) => {
  const response = await axios.post(`${API_URL}/tasks/${id}/assignees`, { userId });
  return response.data?.data;
};

const removeAssigneeAPI = async ({ id, userId }) => {
  const response = await axios.delete(`${API_URL}/tasks/${id}/assignees/${userId}`);
  return response.data?.data;
};

export const useAddTaskAssignee = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: addAssigneeAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["taskDetails", variables?.id] });
      options.onSuccess?.(data, variables, context);
    },
  });
};

export const useRemoveTaskAssignee = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: removeAssigneeAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["taskDetails", variables?.id] });
      options.onSuccess?.(data, variables, context);
    },
  });
};

// ========= Subtasks =========

/** Errors come back as { errorCode, errorMessage }; a few older endpoints still use `message`. */
const serverErrorMessage = (error) =>
  error?.response?.data?.errorMessage || error?.response?.data?.message || null;

/**
 * Surfaces a failed subtask mutation. Without this a rejected request is silent: the row simply
 * stays as it was and the user is left thinking the change went through. A caller that passes its
 * own onError keeps full control, since it owns both the wording and any state it has to unwind.
 */
const reportSubtaskError = (fallback, options) => (error, variables, context) => {
  if (options.onError) {
    options.onError(error, variables, context);
    return;
  }
  toast.error(serverErrorMessage(error) || fallback);
};

const createSubtaskAPI = async ({ id, title, assigneeId, deadline }) => {
  const response = await axios.post(`${API_URL}/tasks/${id}/subtasks`, {
    title,
    assigneeId,
    deadline,
  });
  return response.data?.data;
};

const updateSubtaskAPI = async ({ id, subtaskId, ...changes }) => {
  const response = await axios.patch(`${API_URL}/tasks/${id}/subtasks/${subtaskId}`, changes);
  return response.data?.data;
};

const deleteSubtaskAPI = async ({ id, subtaskId }) => {
  const response = await axios.delete(`${API_URL}/tasks/${id}/subtasks/${subtaskId}`);
  return response.data;
};

const createSubtasksBulkAPI = async ({ id, titles = [] }) => {
  const created = [];
  for (const title of titles) {
    try {
      created.push(await createSubtaskAPI({ id, title, assigneeId: null, deadline: null }));
    } catch (error) {
      error.createdCount = created.length;
      error.remainingTitles = titles.slice(created.length);
      throw error;
    }
  }
  return created;
};

export const useCreateSubtask = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: createSubtaskAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["taskDetails", variables?.id] });
      // Table/Kanban views read subtask progress off the cached tasks list, so it
      // needs to be invalidated too or their "done/total" badges go stale.
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options.onSuccess?.(data, variables, context);
    },
    onError: reportSubtaskError("Could not add the subtask. Please try again.", options),
  });
};

export const useCreateSubtasksBulk = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: createSubtasksBulkAPI,
    // onSettled rather than onSuccess: a batch that fails part-way still persisted
    // everything before the failure, so the caches are stale either way.
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["taskDetails", variables?.id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options.onSettled?.(data, error, variables, context);
    },
    onError: reportSubtaskError("Could not add the subtasks. Please try again.", options),
  });
};

export const useUpdateSubtask = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: updateSubtaskAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["taskDetails", variables?.id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options.onSuccess?.(data, variables, context);
    },
    onError: reportSubtaskError("Could not update the subtask. Please try again.", options),
  });
};

export const useDeleteSubtask = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: deleteSubtaskAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["taskDetails", variables?.id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      options.onSuccess?.(data, variables, context);
    },
    onError: reportSubtaskError("Could not delete the subtask. Please try again.", options),
  });
};

// ========= Comments (with @mention support) =========

const getTaskCommentsAPI = async (taskId) => {
  const response = await axios.get(`${API_URL}/tasks/${taskId}/comments`);
  return Array.isArray(response.data?.data) ? response.data.data : [];
};

const postTaskCommentAPI = async ({ taskId, text, mentionedUserIds = [], attachments = [] }) => {
  const response = await axios.post(`${API_URL}/tasks/${taskId}/comments`, {
    text,
    mentionedUserIds,
    attachments,
  });
  return response.data?.data;
};

const deleteTaskCommentAPI = async ({ taskId, commentId }) => {
  const response = await axios.delete(`${API_URL}/tasks/${taskId}/comments/${commentId}`);
  return response.data;
};

export const useTaskComments = (taskId, options = {}) => {
  return useQuery({
    queryKey: ["taskComments", taskId],
    queryFn: () => getTaskCommentsAPI(taskId),
    enabled: !!taskId,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: false,
    initialData: [],
    ...options,
  });
};

export const usePostTaskComment = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: postTaskCommentAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["taskComments", variables?.taskId] });
      options.onSuccess?.(data, variables, context);
    },
  });
};

export const useDeleteTaskComment = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: deleteTaskCommentAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["taskComments", variables?.taskId] });
      options.onSuccess?.(data, variables, context);
    },
  });
};

const getSubtaskNotesAPI = async ({ id, subtaskId }) => {
  const response = await axios.get(`${API_URL}/tasks/${id}/subtasks/${subtaskId}/notes`);
  return response.data?.data || [];
};

export const useSubtaskNotes = (id, subtaskId, options = {}) =>
  useQuery({
    queryKey: ["subtaskNotes", id, subtaskId],
    queryFn: () => getSubtaskNotesAPI({ id, subtaskId }),
    enabled: Boolean(id && subtaskId),
    ...options,
  });

const createSubtaskNoteAPI = async ({ id, subtaskId, body, attachments = [] }) => {
  const response = await axios.post(`${API_URL}/tasks/${id}/subtasks/${subtaskId}/notes`, {
    body,
    attachments,
  });
  return response.data?.data;
};

const updateSubtaskNoteAPI = async ({ id, subtaskId, noteId, body, attachments = [] }) => {
  const response = await axios.patch(
    `${API_URL}/tasks/${id}/subtasks/${subtaskId}/notes/${noteId}`,
    { body, attachments }
  );
  return response.data?.data;
};

const deleteSubtaskNoteAPI = async ({ id, subtaskId, noteId }) => {
  const response = await axios.delete(
    `${API_URL}/tasks/${id}/subtasks/${subtaskId}/notes/${noteId}`
  );
  return response.data;
};

const subtaskNoteMutation =
  (mutationFn) =>
  (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn,
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({
          queryKey: ["subtaskNotes", variables?.id, variables?.subtaskId],
        });
        options.onSuccess?.(data, variables, context);
      },
    });
  };

export const useCreateSubtaskNote = subtaskNoteMutation(createSubtaskNoteAPI);
export const useUpdateSubtaskNote = subtaskNoteMutation(updateSubtaskNoteAPI);
export const useDeleteSubtaskNote = subtaskNoteMutation(deleteSubtaskNoteAPI);

// ========= Admin escalation =========

const notifyAdminAPI = async ({ id, message }) => {
  const response = await axios.post(`${API_URL}/tasks/${id}/notify-admin`, { message });
  return response.data?.data;
};

export const useNotifyAdminAboutTask = (options = {}) => {
  return useMutation({
    mutationFn: notifyAdminAPI,
    onSuccess: options.onSuccess,
    onError: options.onError,
    ...options,
  });
};

// ========= Export API functions for direct use if needed =========

export const getTasks = getTasksAPI;
export const getTaskById = getTaskByIdAPI;
export const createTask = createTaskAPI;
export const updateTask = (id, taskData) => updateTaskAPI({ id, taskData });
export const deleteTask = deleteTaskAPI;
